import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface CarteraKPIs {
  total_clientes: number;
  con_mora_field: number;
  en_mora_count: number;
  en_mora_pct: number;
  monto_adeudado: number;                 // legacy alias = solo cuotas
  monto_cuotas_en_mora: number;
  monto_fees_pendientes: number;
  deuda_total: number;                    // cuotas + fees C001 + fees C004
  buckets: { [range: string]: { count: number; monto: number } };
  fees: {
    c001_pendientes_count: number;
    c001_pendientes_monto: number;
    c004_pendientes_count: number;
    c004_pendientes_monto: number;
    pendientes_total_monto: number;
    pagados_lifetime_count: number;
    pagados_lifetime_monto: number;
  };
  estado: {
    bloqueados_total: number;
    bloqueo_manual: number;
    reactivation_blocked: number;
    reactivados_alguna_vez: number;
  };
  freshness: {
    last_recalc_max: string;
    last_recalc_min: string;
    stale_minutes: number | null;
  };
  elapsed_ms: number;
}

export interface ClienteItem {
  id: string;
  code: string;
  name: string;
  lastName: string;
  full_name: string;
  identity?: string;
  quota_active: boolean;
  user_blocked_manually: boolean;
  reactivation_blocked: boolean;
  penalty_reactivation_count: number;
  mora_dias: number;
  mora_count: number;
  monto_adeudado: number;
  fees_c001_pend_count: number;
  fees_c001_pend_monto: number;
  fees_c004_pend_count: number;
  fees_c004_pend_monto: number;
  tendencia: string | null;
  proxima_cuota: any | null;
  last_recalc: string;
}

export interface ClienteListResponse {
  items: ClienteItem[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
  elapsed_ms: number;
}

export interface ClienteFilters {
  dias_min?: number | null;
  dias_max?: number | null;
  estado?: 'activo' | 'bloqueado' | '';
  bloqueo_tipo?: 'manual' | 'automatico' | 'reactivacion' | '';
  tiene_reactivaciones?: 'true' | 'false' | '';
  monto_min?: number | null;
  search?: string;
  sort_by?: string;
  sort_dir?: 1 | -1;
  page?: number;
  page_size?: number;
}

export interface ClienteDetail {
  id: string;
  code: string;
  name: string;
  lastName: string;
  full_name: string;
  identity?: string;
  email?: string;
  phone?: string;
  quota_active: boolean;
  user_blocked_manually: boolean;
  reactivation_blocked: boolean;
  reactivation_block_reason: string | null;
  penalty_reactivation_count: number;
  penalty_reactivation_history: any[];
  mora: any;
  last_mora_recalculated_at: string;
  fees_pendientes: any[];
  fees_pagados_recientes: any[];
  cuotas_en_mora: any[];
  cuotas_pagadas_recientes?: any[];   // NUEVO: cuotas pagadas con fee_status (Ajuste detalle por cuota)
  elapsed_ms: number;
}

export interface DistribucionMora {
  buckets: Array<{ range: string; min: number | null; count: number; monto: number }>;
  elapsed_ms: number;
}

@Injectable({ providedIn: 'root' })
export class CarteraService {
  private base = `${environment.BASE_LA}/api/cartera`;

  constructor(private http: HttpClient) {}

  getKPIs() {
    return lastValueFrom(this.http.get<{ ok: boolean; data: CarteraKPIs }>(`${this.base}/kpis`));
  }

  getIndicadoresMorosidad() {
    return lastValueFrom(this.http.get<{ ok: boolean; data: any }>(`${this.base}/indicadores/morosidad`));
  }

  getDistribucionDinero() {
    return lastValueFrom(this.http.get<{ ok: boolean; data: {
      grupos: Array<{ id: string; label: string; count_cuotas: number; monto_pendiente: number; count_personas: number; color: string }>;
      totales: { count_cuotas: number; monto_pendiente: number; note?: string };
      meta?: any;
    } }>(`${this.base}/distribucion-dinero`));
  }

  getCarteraConfig() {
    return lastValueFrom(this.http.get<{ ok: boolean; data: any; defaults: any }>(`${this.base}/config`));
  }

  setCarteraConfig(params: any) {
    return lastValueFrom(this.http.put<{ ok: boolean; data: any }>(`${this.base}/config`, params));
  }

  previewCarteraConfig(params: any) {
    return lastValueFrom(this.http.post<{ ok: boolean; data: any }>(`${this.base}/config/preview`, params));
  }

  getComportamientoPago(days: number = 30) {
    return lastValueFrom(this.http.get<{ ok: boolean; data: any }>(`${this.base}/indicadores/comportamiento`, { params: { days: String(days) }}));
  }

  getGestionCobro(desdeYear: number = 2023, diasHistorico: number = 90) {
    return lastValueFrom(this.http.get<{ ok: boolean; data: any }>(`${this.base}/indicadores/gestion-cobro`, {
      params: { desde_year: String(desdeYear), dias_historico: String(diasHistorico) }
    }));
  }

  triggerSnapshotNow() {
    return lastValueFrom(this.http.post<{ ok: boolean; data: any }>(`${this.base}/snapshot/trigger`, {}));
  }

  listClientes(filters: ClienteFilters = {}) {
    let params = new HttpParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v != null && v !== '') params = params.set(k, String(v));
    });
    return lastValueFrom(this.http.get<{ ok: boolean; data: ClienteListResponse }>(`${this.base}/clientes`, { params }));
  }

  getCliente(id: string) {
    return lastValueFrom(this.http.get<{ ok: boolean; data: ClienteDetail }>(`${this.base}/cliente/${id}`));
  }

  getDistribucion() {
    return lastValueFrom(this.http.get<{ ok: boolean; data: DistribucionMora }>(`${this.base}/distribucion-mora`));
  }

  getActividad(limit = 20) {
    let params = new HttpParams().set('limit', String(limit));
    return lastValueFrom(this.http.get<{ ok: boolean; data: { runs: any[] } }>(`${this.base}/actividad`, { params }));
  }

  // ── Análisis ─────────────────────────────────────
  getAnalisisRollRate()       { return lastValueFrom(this.http.get<{ ok: boolean; data: any[] }>(`${this.base}/analisis/roll-rate`)); }
  getAnalisisCureRate()       { return lastValueFrom(this.http.get<{ ok: boolean; data: any[] }>(`${this.base}/analisis/cure-rate`)); }
  getAnalisisVintage()        { return lastValueFrom(this.http.get<{ ok: boolean; data: any[] }>(`${this.base}/analisis/vintage`)); }
  getAnalisisECL()            { return lastValueFrom(this.http.get<{ ok: boolean; data: any[] }>(`${this.base}/analisis/ecl`)); }
  getAnalisisPorAliado()      { return lastValueFrom(this.http.get<{ ok: boolean; data: any[] }>(`${this.base}/analisis/por-aliado`)); }
  getAnalisisPrediccion()     { return lastValueFrom(this.http.get<{ ok: boolean; data: any[] }>(`${this.base}/analisis/prediccion`)); }
  getAnalisisTopCriticos()    { return lastValueFrom(this.http.get<{ ok: boolean; data: any[] }>(`${this.base}/analisis/top-criticos`)); }
  getAnalisisReactivaciones() { return lastValueFrom(this.http.get<{ ok: boolean; data: any[] }>(`${this.base}/analisis/reactivaciones`)); }
  refreshAnalisisCache()      { return lastValueFrom(this.http.post<{ ok: boolean }>(`${this.base}/analisis/refresh-cache`, {})); }

  getCliente360(codeOrId: string) {
    return lastValueFrom(this.http.get<{ ok: boolean; data: any }>(`${this.base}/cliente-360/${codeOrId}`));
  }

  getAnomaliasResumen() {
    return lastValueFrom(this.http.get<{ ok: boolean; data: {
      cutoff: string;
      sin_recalc_hoy: number;
      cuotas_sin_fee: number;
      mora_incoherente: number;
      bloqueados_sin_mora: number;
      criticos_180: { count: number; monto: number };
    } }>(`${this.base}/anomalias`));
  }

  getAnomaliaList(tipo: string, page = 1, pageSize = 100) {
    let params = new HttpParams().set('page', String(page)).set('page_size', String(pageSize));
    return lastValueFrom(this.http.get<{ ok: boolean; tipo: string; title: string; total: number; items: any[] }>(`${this.base}/anomalias/${tipo}`, { params }));
  }

  downloadAnomaliaXlsx(tipo: string): string {
    return `${this.base}/anomalias/${tipo}/xlsx`;
  }

  getEscalafonFees() {
    return lastValueFrom(this.http.get<{ ok: boolean; data: {
      total_fees: number; total_monto: number;
      breakdown: Array<{ concepto: string; monto_fee: number; count: number; monto_total: number; due_balance_total: number; clientes_unicos: number }>;
    } }>(`${this.base}/escalafon-fees`));
  }

  // ── Acciones cliente ─────────────────────────────
  recalcCliente(id: string) {
    return lastValueFrom(this.http.post<{ ok: boolean; data: { recalculated: boolean; elapsed_ms: number } }>(`${this.base}/cliente/${id}/recalc`, {}));
  }
  toggleBloqueoManual(id: string, blocked: boolean, reason?: string) {
    return lastValueFrom(this.http.post<{ ok: boolean; data: { blocked: boolean } }>(`${this.base}/cliente/${id}/bloqueo`, { blocked, reason }));
  }
  recalcBatch(ids: string[]) {
    return lastValueFrom(this.http.post<{ ok: boolean; data: { ok: number; failed: number; items: any[] } }>(`${this.base}/recalc-batch`, { ids }));
  }

  // ── Operación ────────────────────────────────────
  getMovimientosHoy(opts: { preset?: string; from?: string; to?: string } = {}) {
    let params = new HttpParams();
    if (opts.preset) params = params.set('preset', opts.preset);
    if (opts.from) params = params.set('from', opts.from);
    if (opts.to) params = params.set('to', opts.to);
    return lastValueFrom(this.http.get<{ ok: boolean; data: {
      rango: { from: string; to: string };
      resumen: { creados_hoy: number; desactivados_hoy: number; actualizados_hoy: number; c001_creados: number; c004_creados: number };
      creados: any[]; desactivados: any[]; actualizados: any[];
    } }>(`${this.base}/operacion/movimientos-hoy`, { params }));
  }

  getRecaudacion(opts: { preset?: string; from?: string; to?: string } = {}) {
    let params = new HttpParams();
    if (opts.preset) params = params.set('preset', opts.preset);
    if (opts.from) params = params.set('from', opts.from);
    if (opts.to) params = params.set('to', opts.to);
    return lastValueFrom(this.http.get<{ ok: boolean; data: {
      rango: { from: string; to: string };
      total_monto: number; total_count: number;
      por_concepto: Array<{ concepto: string; concepto_nombre: string; count: number; monto: number; clientes_unicos: number }>;
      top_clientes: Array<{ id: string; code: string; full_name: string; identity: string; count: number; monto: number }>;
      serie_diaria: Array<{ fecha: string; count: number; monto: number }>;
    } }>(`${this.base}/operacion/recaudacion`, { params }));
  }
  getProximosC004(rangoMin = 50, rangoMax = 60, limit = 200) {
    let params = new HttpParams().set('rangoMin', String(rangoMin)).set('rangoMax', String(rangoMax)).set('limit', String(limit));
    return lastValueFrom(this.http.get<{ ok: boolean; data: { rango: any; total: number; clientes: any[] } }>(`${this.base}/operacion/proximos-c004`, { params }));
  }
  getAging() {
    return lastValueFrom(this.http.get<{ ok: boolean; data: {
      total_clientes: number; total_monto: number;
      buckets: Array<{ rango: string; min: number | null; clientes: number; pct_clientes: number; monto_pendiente: number; pct_monto: number; fees_c001: number; fees_c001_monto: number; fees_c004: number; fees_c004_monto: number }>;
    } }>(`${this.base}/operacion/aging`));
  }
}
